import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import invariant from 'tiny-invariant';

import type { EditorRefHandle } from '~/components';

import { forumTags, isAdminOnlyForumTag } from '~/api/forum/constants';
import { useCreateForumPost } from '~/api/forum/useCreateForumPost';
import { Button, Dialog, Illustration, Link, RichTextEditor, Text } from '~/components';
import { Field, Form, Input, Select } from '~/components/Form';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { persistFormDataInSession, removeFormDataFromSession } from '~/utils/form';
import { mergeRefs } from '~/utils/mergeRefs';
import { isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

import type { CreateForumPostForm } from './CreateForumPostDialog.types';

import { useForumTagFilter } from '../Forum.nuqs';
import { illustrationForForumTag } from '../Forum.utils';
import { formStyle, tagIllustrationStyle, tagOptionStyle } from './CreateForumPostDialog.css';
import { useForumPostPersistedFormValues } from './CreateForumPostDialog.hooks';

type Props = {
  onClose: () => void;
};

export const CreateForumPostDialog = ({ onClose }: Props) => {
  const { t } = useTranslation(['forum']);

  return (
    <Dialog.Body>
      <Dialog.Header title={t('forum:createPost.title')} />
      <CreateForumPostDialogContent onClose={onClose} />
    </Dialog.Body>
  );
};

const CreateForumPostDialogContent = ({ onClose }: Pick<Props, 'onClose'>) => {
  const mutation = useCreateForumPost();

  const [activeTag] = useForumTagFilter();

  const form = useForm<CreateForumPostForm>({
    defaultValues: {
      tag: activeTag !== null && !isAdminOnlyForumTag(activeTag) ? activeTag : null,
      title: '',
      content: { type: 'doc', content: [] },
    },
  });
  const { control, getValues, handleSubmit, watch } = form;

  const editorRef = useRef<EditorRefHandle>(null);
  useForumPostPersistedFormValues(form, editorRef);

  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { t } = useTranslation(['forum', 'validation']);

  const onSubmit = handleSubmit((data) => {
    invariant(data.tag);

    mutation.mutate(
      { forumTag: data.tag, title: data.title, content: JSON.stringify(data.content) },
      {
        onSuccess: (forumPost) => {
          toast.success({ description: t('forum:alerts.createPost.success') });
          onClose();
          removeFormDataFromSession(routes.forum);
          navigate(r(routes.forumPost, { postId: forumPost.id }), { state: { from: `${pathname}${search}` } });
        },
        onError: () => toast.danger({ description: t('forum:alerts.createPost.error') }),
      },
    );
  });

  useEffect(() => {
    persistFormDataInSession(routes.forum, getValues());

    const subscription = watch((data) => persistFormDataInSession(routes.forum, data));

    return subscription.unsubscribe;
  }, [getValues, watch]);

  const tagItems = forumTags.filter((tag) => !isAdminOnlyForumTag(tag)).map((tag) => ({ label: t(`forum:tags.${tag}.name`), value: tag }));

  return (
    <Form className={formStyle} onSubmit={onSubmit}>
      <Text>
        <Trans
          components={{ guidelinesLink: <Link onClick={onClose} to={routes.forumGuidelines} /> }}
          i18nKey="forum:createPost.disclaimer"
        />
      </Text>

      <Controller
        control={control}
        name="tag"
        render={({ field: { name, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label>{t('forum:createPost.fields.tag.label')}</Field.Label>
            <Field.Description>{t('forum:createPost.fields.tag.description')}</Field.Description>

            <Select.Root items={tagItems} onValueChange={onChange} value={value}>
              <Select.Button ref={ref} />
              <Select.List>
                {tagItems.map(({ label, value }) => (
                  <Select.Item key={value} value={value}>
                    <div className={tagOptionStyle}>
                      <Illustration className={tagIllustrationStyle} name={illustrationForForumTag(value)} strokeWidth={1} />
                      {label}
                    </div>
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Root>
            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{ required: { value: true, message: t('validation:required') } }}
      />

      <Controller
        control={control}
        name="title"
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label>{t('forum:createPost.fields.title.label')}</Field.Label>
            <Input onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />
            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{
          required: { value: true, message: t('validation:required') },
          validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
          maxLength: { value: 200, message: t('validation:maxLength', { attribute: t('forum:createPost.fields.title.label'), max: 200 }) },
        }}
      />

      <Controller
        control={control}
        name="content"
        render={({ field: { name, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label>{t('forum:createPost.fields.content.label')}</Field.Label>
            <RichTextEditor isInvalid={invalid} mode="json" onChange={onChange} ref={mergeRefs(editorRef, ref)} value={value} />
            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{ validate: { notEmpty: () => (editorRef.current?.isEmpty() ? t('validation:required') : true) } }}
      />

      <Dialog.Actions>
        <Button icon="Send" loading={mutation.isPending} type="submit">
          {t('forum:createPost.actions.post')}
        </Button>
      </Dialog.Actions>
    </Form>
  );
};
